import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Snackbar,
  Breadcrumbs,
  Tabs,
  Tab,
  TablePagination
} from "@mui/material";
import {
  ArrowBack,
  Search,
  Refresh,
  Assignment,
  Business,
  EmojiEvents,
  Person,
  Mail,
  CalendarToday,
  Stars,
  Star,
  CheckCircle,
  AccountCircle,
  FolderSpecial,
  PlaylistAddCheck
} from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const TeamContributionOverview = () => {
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [teamList, setTeamList] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [managerEmployeeId, setManagerEmployeeId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const getCompanyId = () => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.company_id;
    } catch (error) {
      //console.error("Error parsing token:", error);
      return null;
    }
  };

  const fetchManagerId = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      if (response.data && response.data.employee_id) {
        setManagerEmployeeId(response.data.employee_id);
        return response.data.employee_id;
      }
      return null;
    } catch (err) {
      //console.error("Error fetching current user:", err);
      return null;
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch the list of team members and their metrics summary
  const fetchTeamList = async () => {
    try {
      setLoading(true);
      const managerId = managerEmployeeId || await fetchManagerId();
      if (!managerId) {
        showSnackbar("Could not resolve manager identification.", "error");
        return;
      }

      const response = await api.get(`/api/manager/team-contributions/list?employee_id=${managerId}&page=${page}&limit=${limit}`);
      if (response.data && response.data.status === "success") {
        setTeamList(response.data.data);
        if (response.data.pagination) {
          setTotalCount(response.data.pagination.total);
        } else {
          setTotalCount(response.data.data.length);
        }
      }
    } catch (error) {
      //console.error("Error fetching team list:", error);
      showSnackbar("Failed to load team contributions list", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch complete details for a single selected employee
  const fetchEmployeeDetails = async (empId) => {
    try {
      setDetailLoading(true);
      const response = await api.get(`/api/manager/team-contributions/${empId}`);
      if (response.data && response.data.status === "success") {
        setEmployeeDetails(response.data.data);
        setSelectedEmployeeId(empId);
        setActiveTab(0); // Reset to first tab
      }
    } catch (error) {
      //console.error("Error fetching employee details:", error);
      showSnackbar("Failed to load employee contribution details", "error");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamList();
  }, [managerEmployeeId, page, limit]);

  const handleBackToList = () => {
    setSelectedEmployeeId(null);
    setEmployeeDetails(null);
    fetchTeamList();
  };

  const filteredTeamList = teamList.filter(emp =>
    emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTaskStatusLabel = (status) => {
    switch (status) {
      case 1: return { text: "To Do", color: "default" };
      case 2: return { text: "In Progress", color: "warning" };
      case 3: return { text: "Review", color: "info" };
      case 4: return { text: "Done", color: "success" };
      default: return { text: "To Do", color: "default" };
    }
  };

  const getTaskPriorityLabel = (priority) => {
    switch (priority) {
      case 1: return { text: "High", color: "error" };
      case 2: return { text: "Medium", color: "warning" };
      case 3: return { text: "Low", color: "info" };
      default: return { text: "Medium", color: "warning" };
    }
  };

  const getIssueTypeLabel = (level) => {
    switch (level) {
      case 1: return { text: "Epic", color: "#8777D9" };
      case 2: return { text: "Story", color: "#36B37E" };
      case 3: return { text: "Task", color: "#0052CC" };
      case 4: return { text: "Bug", color: "#DE350B" };
      case 5: return { text: "Subtask", color: "#00B8D9" };
      default: return { text: "Task", color: "#0052CC" };
    }
  };

  const getImageUrl = (photoName) => {
    if (!photoName || photoName.trim() === "") return undefined;
    if (photoName.startsWith("http")) return photoName;
    return `${API_URL}/uploads/${photoName.replace(/^\/+/g, "")}`;
  };

  const getTotalTasksCount = () => {
    if (!employeeDetails || !employeeDetails.projects) return 0;
    return employeeDetails.projects.reduce((sum, proj) => sum + (proj.tasks?.length || 0), 0);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2, background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)", color: "#fff" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
           
            <Typography variant="h4" fontWeight="bold">
              Team Contribution
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              Monitor project assignments, task delivery status, cost center allocations, and kudos
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={() => selectedEmployeeId ? fetchEmployeeDetails(selectedEmployeeId) : fetchTeamList()}
            disabled={loading || detailLoading}
            sx={{ bgcolor: "rgba(255, 255, 255, 0.15)", hover: { bgcolor: "rgba(255, 255, 255, 0.25)" } }}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Main View: Team Directory List */}
      {!selectedEmployeeId ? (
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          {/* Search bar */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search team by name, ID, department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search color="action" sx={{ mr: 1 }} />
              }}
              sx={{ width: { xs: "100%", sm: 350 } }}
            />
            <Typography variant="body2" color="text.secondary">
              Total Team Members: {totalCount}
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={8}>
              <CircularProgress />
            </Box>
          ) : filteredTeamList.length === 0 ? (
            <Alert severity="info">No team members found reporting to you.</Alert>
          ) : (
            <Box>
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ bgcolor: "#f5f7fa" }}>
                    <TableRow>
                      <TableCell fontWeight="bold">Employee Name</TableCell>
                      <TableCell fontWeight="bold">Employee ID</TableCell>
                      <TableCell fontWeight="bold">Designation</TableCell>
                      <TableCell fontWeight="bold">Department</TableCell>
                      <TableCell align="center" fontWeight="bold">Assigned Projects</TableCell>
                      <TableCell align="center" fontWeight="bold">Allocated Tasks</TableCell>
                      <TableCell align="center" fontWeight="bold">Cost Centers</TableCell>
                      <TableCell align="center" fontWeight="bold">Client/Peer Kudos</TableCell>
                      <TableCell align="center" fontWeight="bold">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTeamList.map((emp) => (
                      <TableRow key={emp.employee_id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar 
                              src={getImageUrl(emp.personalPhoto)} 
                              sx={{ bgcolor: "#1976d2", fontWeight: "bold" }}
                            >
                              {emp.name?.charAt(0) || "E"}
                            </Avatar>
                            <Typography fontWeight="medium">{emp.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{emp.employee_id}</TableCell>
                        <TableCell>{emp.designation}</TableCell>
                        <TableCell>{emp.department}</TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<FolderSpecial style={{ fontSize: 16 }} />}
                            label={emp.projectCount || "0"}
                            color={emp.projectCount > 0 ? "primary" : "default"}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<Assignment style={{ fontSize: 16 }} />}
                            label={emp.taskCount || "0"}
                            color={emp.taskCount > 0 ? "info" : "default"}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<Business style={{ fontSize: 16 }} />}
                            label={emp.costCenterCount || "0"}
                            color={emp.costCenterCount > 0 ? "secondary" : "default"}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<EmojiEvents style={{ fontSize: 16 }} />}
                            label={emp.kudosCount || "0"}
                            color={emp.kudosCount > 0 ? "warning" : "default"}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => fetchEmployeeDetails(emp.employee_id)}
                            sx={{ textTransform: "none" }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalCount}
                rowsPerPage={limit}
                page={page - 1}
                onPageChange={(e, newPage) => setPage(newPage + 1)}
                onRowsPerPageChange={(e) => {
                  setLimit(parseInt(e.target.value, 10));
                  setPage(1);
                }}
              />
            </Box>
          )}
        </Paper>
      ) : (
        /* Detailed Single-Employee Profile View */
        <Box>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToList}
            variant="outlined"
            sx={{ mb: 3, textTransform: "none" }}
          >
            Back to Team List
          </Button>

          {detailLoading || !employeeDetails ? (
            <Box display="flex" justifyContent="center" p={8}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Profile Card Summary */}
              <Grid item xs={12} md={4}>
                <Card elevation={2} sx={{ height: "100%", borderRadius: 2 }}>
                  <CardContent>
                    <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" pb={3}>
                      <Avatar 
                        src={getImageUrl(employeeDetails.profile?.personalPhoto)} 
                        sx={{ width: 80, height: 80, bgcolor: "#1976d2", fontSize: "2rem", fontWeight: "bold", mb: 2 }}
                      >
                        {employeeDetails.profile?.name?.charAt(0) || "E"}
                      </Avatar>
                      <Typography variant="h5" fontWeight="bold">
                        {employeeDetails.profile?.name}
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary">
                        {employeeDetails.profile?.designation}
                      </Typography>
                      <Chip label={employeeDetails.profile?.department} color="primary" variant="outlined" size="small" sx={{ mt: 1 }} />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Person color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Employee ID</Typography>
                          <Typography variant="body2">{employeeDetails.profile?.employee_id}</Typography>
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Mail color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Email Address</Typography>
                          <Typography variant="body2">{employeeDetails.profile?.email || "N/A"}</Typography>
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1.5}>
                        <CalendarToday color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Date of Joining</Typography>
                          <Typography variant="body2">
                            {employeeDetails.profile?.joiningDate ? new Date(employeeDetails.profile.joiningDate).toLocaleDateString() : "N/A"}
                          </Typography>
                        </Box>
                      </Box>

                      {employeeDetails.profile?.grade && (
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Stars color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">Grade / Level</Typography>
                            <Typography variant="body2">{employeeDetails.profile.grade}</Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {employeeDetails.profile?.selectedResponsibilities?.length > 0 && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Key Responsibilities
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {employeeDetails.profile.selectedResponsibilities.map((resp, idx) => (
                            <Chip key={idx} label={resp} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Tabbed content & KPI totals */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  {/* Summary Metric Counters */}
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ bgcolor: "#e3f2fd", borderRadius: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 3 }}>
                        <Typography variant="h3" color="primary.main" fontWeight="bold">
                          {employeeDetails.projects?.length || 0}
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                          Projects Assigned
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Card sx={{ bgcolor: "#e8f5e9", borderRadius: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 3 }}>
                        <Typography variant="h3" color="success.main" fontWeight="bold">
                          {getTotalTasksCount()}
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                          Allocated Tasks
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Card sx={{ bgcolor: "#fff8e1", borderRadius: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 3 }}>
                        <Typography variant="h3" color="warning.main" fontWeight="bold">
                          {employeeDetails.kudos?.length || 0}
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                          Client/Peer Kudos
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Tabbed Detailed Information Panel */}
                  <Grid item xs={12}>
                    <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
                                          <Tabs
                        value={activeTab}
                        onChange={(e, val) => setActiveTab(val)}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#fcfcfc" }}
                      >
                        <Tab 
                          label={`Cost Centers (${employeeDetails.costCenters?.length || 0})`} 
                          icon={<Business fontSize="small" />} 
                          iconPosition="start" 
                          sx={{ py: 2, textTransform: "none", fontWeight: "bold" }}
                        />
                        <Tab 
                          label={`Projects & Tasks (${employeeDetails.projects?.length || 0})`} 
                          icon={<FolderSpecial fontSize="small" />} 
                          iconPosition="start" 
                          sx={{ py: 2, textTransform: "none", fontWeight: "bold" }}
                        />
                        <Tab 
                          label={`Kudos & Recognition (${employeeDetails.kudos?.length || 0})`} 
                          icon={<EmojiEvents fontSize="small" />} 
                          iconPosition="start" 
                          sx={{ py: 2, textTransform: "none", fontWeight: "bold" }}
                        />
                      </Tabs>

                      
                      <Box sx={{ p: 3 }}>
                        {/* Tab 1: Cost Centers Worked In */}
                        {activeTab === 0 && (
                          <Box>
                            <Typography variant="h6" fontWeight="bold" gutterBottom display="flex" alignItems="center" gap={1}>
                              <Business color="primary" /> Cost Centers Worked In
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {employeeDetails.costCenters?.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No active cost centers are assigned to this employee.
                              </Typography>
                            ) : (
                              <Grid container spacing={2}>
                                {employeeDetails.costCenters.map((cc) => (
                                  <Grid item xs={12} sm={6} key={cc.id}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "#fafafa" }}>
                                      <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                          {cc.name}
                                        </Typography>
                                        <Chip label={cc.code} size="small" color="primary" />
                                      </Box>
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Department: {cc.department || "N/A"}
                                      </Typography>
                                      {cc.description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                          {cc.description}
                                        </Typography>
                                      )}
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                            )}
                          </Box>
                        )}

                        {/* Tab 2: Assigned Projects & Allocated Tasks */}
                        {activeTab === 1 && (
                          <Box>
                            <Typography variant="h6" fontWeight="bold" gutterBottom display="flex" alignItems="center" gap={1}>
                              <FolderSpecial color="primary" /> Assigned Projects & Allocated Tasks
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {employeeDetails.projects?.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                This employee is currently not assigned to any projects.
                              </Typography>
                            ) : (
                              <Box display="flex" flexDirection="column" gap={3}>
                                {employeeDetails.projects.map((proj, idx) => (
                                  <Paper key={proj.project_id} variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                                    <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1.5}>
                                      <Box>
                                        <Typography variant="subtitle1" fontWeight="bold" color="primary" display="inline">
                                          {idx + 1}. {proj.name}
                                        </Typography>
                                        <Chip label={proj.key} size="small" sx={{ ml: 1, bgcolor: "#e3f2fd" }} />
                                        <Chip label={proj.projectType} size="small" variant="outlined" sx={{ ml: 0.5 }} />
                                      </Box>
                                      <Typography variant="body2" color="text.secondary">
                                        <strong>Assignment Period:</strong> {new Date(proj.start_date).toLocaleDateString()}
                                        {proj.end_date ? ` to ${new Date(proj.end_date).toLocaleDateString()}` : ' to Present'}
                                      </Typography>
                                    </Box>

                                    <Grid container spacing={2} sx={{ mb: 2, bgcolor: "#f8fafc", p: 1.5, borderRadius: 1.5 }}>
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="body2">
                                          <strong>Assigned Role:</strong> {proj.role || "Team Member"}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="body2">
                                          <strong>Allocation Percentage:</strong> {proj.allocation_percentage}%
                                        </Typography>
                                      </Grid>
                                      {proj.description && (
                                        <Grid item xs={12}>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            <strong>Project Description:</strong> {proj.description}
                                          </Typography>
                                        </Grid>
                                      )}
                                    </Grid>

                                    {/* Allocated tasks table */}
                                    <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom display="flex" alignItems="center" gap={0.5}>
                                      <PlaylistAddCheck fontSize="small" /> Allocated Project Tickets / Tasks ({proj.tasks?.length || 0})
                                    </Typography>

                                    {proj.tasks?.length === 0 ? (
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, pl: 1, fontStyle: "italic" }}>
                                        No active tasks/issues assigned to this employee under this project.
                                      </Typography>
                                    ) : (
                                      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, borderRadius: 1.5 }}>
                                        <Table size="small">
                                          <TableHead sx={{ bgcolor: "#fafafa" }}>
                                            <TableRow>
                                              <TableCell fontWeight="bold">Key</TableCell>
                                              <TableCell fontWeight="bold">Type</TableCell>
                                              <TableCell fontWeight="bold">Task Summary</TableCell>
                                              <TableCell align="center" fontWeight="bold">Priority</TableCell>
                                              <TableCell align="center" fontWeight="bold">Status</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {proj.tasks.map((task) => {
                                              const statusMeta = getTaskStatusLabel(task.status);
                                              const priorityMeta = getTaskPriorityLabel(task.priority);
                                              const typeMeta = getIssueTypeLabel(task.hierarchy_level);
                                              return (
                                                <TableRow key={task.id} hover>
                                                  <TableCell sx={{ fontWeight: "medium" }}>{task.key}</TableCell>
                                                  <TableCell>
                                                    <Chip
                                                      label={typeMeta.text}
                                                      size="small"
                                                      style={{
                                                        backgroundColor: typeMeta.color,
                                                        color: "#fff",
                                                        fontSize: "0.75rem",
                                                        fontWeight: "bold",
                                                        height: 20
                                                      }}
                                                    />
                                                  </TableCell>
                                                  <TableCell>
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                                                      {task.summary}
                                                    </Typography>
                                                  </TableCell>
                                                  <TableCell align="center">
                                                    <Chip
                                                      label={priorityMeta.text}
                                                      color={priorityMeta.color}
                                                      size="small"
                                                      variant="outlined"
                                                      sx={{ fontSize: "0.75rem", height: 20 }}
                                                    />
                                                  </TableCell>
                                                  <TableCell align="center">
                                                    <Chip
                                                      label={statusMeta.text}
                                                      color={statusMeta.color}
                                                      size="small"
                                                      sx={{ fontSize: "0.75rem", height: 20 }}
                                                    />
                                                  </TableCell>
                                                </TableRow>
                                              );
                                            })}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    )}
                                  </Paper>
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Tab 3: Kudos & Peer Appreciations */}
                        {activeTab === 2 && (
                          <Box>
                            <Typography variant="h6" fontWeight="bold" gutterBottom display="flex" alignItems="center" gap={1}>
                              <EmojiEvents color="primary" /> Client & Peer Recognitions (Kudos)
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {employeeDetails.kudos?.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No kudos or formal client recognitions have been posted for this employee yet.
                              </Typography>
                            ) : (
                              <Grid container spacing={2}>
                                {employeeDetails.kudos.map((item) => (
                                  <Grid item xs={12} key={item.id}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: "flex", gap: 2 }}>
                                      <Avatar sx={{ bgcolor: "#fff3e0", color: "#ff9800", alignSelf: "flex-start", width: 44, height: 44 }}>
                                        <Star />
                                      </Avatar>
                                      <Box flex={1}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                                          <Typography variant="subtitle2" fontWeight="bold">
                                            Appreciated by: {item.sender_name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {new Date(item.createdAt).toLocaleDateString()}
                                          </Typography>
                                        </Box>
                                        {item.metadata?.badge && (
                                          <Chip
                                            icon={<Stars style={{ color: "#ffb300" }} />}
                                            label={item.metadata.badge}
                                            size="small"
                                            sx={{ my: 1, bgcolor: "#fffde7", border: "1px solid #fff59d" }}
                                          />
                                        )}
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: "italic" }}>
                                          "{item.content}"
                                        </Typography>
                                      </Box>
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* Snackbar Alert */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TeamContributionOverview;
